import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/lib/auth";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const http = axios.create({ baseURL: `${BACKEND}/api`, withCredentials: true, timeout: 20000 });

const Ctx = createContext({ values: {}, catalog: {}, refresh: async () => {}, getSetting: () => "" });

export function AppSettingsProvider({ children }) {
  const { user } = useAuth();
  const [data, setData] = useState({ values: {}, global: {}, user_overrides: {}, catalog: {} });

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await http.get("/settings/me");
      setData(data);
    } catch { /* fallback silencioso */ }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Aplica primary color como CSS var sempre que muda
  useEffect(() => {
    const c = data?.values?.["app.brand.primary_color"];
    if (c) document.documentElement.style.setProperty("--brand-primary", c);
  }, [data]);

  const getSetting = useCallback((key, fallback = "") => {
    if (data?.values && key in data.values) return data.values[key];
    return data?.catalog?.[key]?.default ?? fallback;
  }, [data]);

  const value = useMemo(() => ({ ...data, refresh, getSetting }), [data, refresh, getSetting]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAppSettings() { return useContext(Ctx); }

export function useSetting(key, fallback = "") {
  const { getSetting } = useAppSettings();
  return getSetting(key, fallback);
}

// API helpers para admin
export const Settings = {
  me: () => http.get("/settings/me").then(r => r.data),
  patchUser: (changes) => http.patch("/settings/me/branding", changes).then(r => r.data),
  adminList: () => http.get("/admin/settings").then(r => r.data),
  adminApply: (items, source = "manual", prompt = null) =>
    http.post("/admin/settings/apply", { items, source, prompt }).then(r => r.data),
  adminAiSuggest: (prompt) => http.post("/admin/settings/ai/suggest", { prompt }, { timeout: 90000 }).then(r => r.data),
  adminHistory: () => http.get("/admin/settings/history").then(r => r.data),
  adminRollback: (batch_id) => http.post("/admin/settings/rollback", { batch_id }).then(r => r.data),
};
